import { Link } from '@remix-run/react';
import { json, type LoaderFunctionArgs, type MetaFunction } from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';
import {
  ArrowRightIcon,
  ClockIcon,
  CheckCircleIcon,
  LightBulbIcon,
  HomeIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import Header from '~/components/Header';
import Footer from '~/components/Footer';
import {
  generateCanonicalUrl,
  generateBreadcrumbJsonLd,
  SITE_NAME,
} from '~/utils/seo';
import FAQ from '~/components/FAQ';
import { BLOG_CACHE_HEADERS } from '~/utils/cacheHeaders';
import { getAuthenticatedUser } from '~/utils/supabase.server';

export const maxDuration = 300;

const SLUG = 'how-to-make-a-short-form-video-in-under-30-minutes';
const TITLE = 'How to Make a Short-Form Video in Under 30 Minutes in 2026';

export const meta: MetaFunction = () => {
  const canonicalUrl = generateCanonicalUrl(`/blog/${SLUG}`);
  const metaTitle = 'How to Make a Short-Form Video in 30 Minutes';
  const description =
    "Learn how to make a short-form video in under 30 minutes with a simple 6-step workflow. Follow Thetabber's guide today.";

  return [
    { title: `${metaTitle} | ${SITE_NAME}` },
    { name: 'description', content: description },
    {
      name: 'keywords',
      content:
        'how to make a short form video, short form video in 30 minutes, vertical video workflow, tiktok reels shorts video, caption short video, record short form video fast',
    },
    { property: 'og:title', content: `${metaTitle} | ${SITE_NAME}` },
    { property: 'og:description', content: description },
    { property: 'og:type', content: 'article' },
    { property: 'og:url', content: canonicalUrl },
    {
      property: 'og:image',
      content: generateCanonicalUrl('/blog/how-to-make-a-short-form-video-hero.png'),
    },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: `${metaTitle} | ${SITE_NAME}` },
    { name: 'twitter:description', content: description },
    {
      name: 'twitter:image',
      content: generateCanonicalUrl('/blog/how-to-make-a-short-form-video-hero.png'),
    },
    { tagName: 'link', rel: 'canonical', href: canonicalUrl },
  ];
};

export async function loader({ request }: LoaderFunctionArgs) {
  const user = await getAuthenticatedUser(request);

  return json({ isLoggedIn: !!user }, { headers: BLOG_CACHE_HEADERS });
}

const FAQS = [
  {
    question: 'How do I make a short-form video for TikTok, Reels, and Shorts at once?',
    answer:
      'Record and export one 9:16 vertical video, then write a separate caption for each platform. Keep your text and your face toward the center of the frame so platform buttons do not cover them. The video file can be identical everywhere. The caption, title, and call to action should be written for each network and how its caption display works.',
  },
  {
    question: 'How long should a short-form video be?',
    answer:
      'Start at 30 to 60 seconds. In Wyzowl\'s 2026 survey, 51% of video marketers said that range works best, and 71% chose something between 30 seconds and two minutes. After a few weeks, use your own completion rates to adjust, since the right length depends on topic, platform, and audience.',
  },
  {
    question: 'What is short-form video?',
    answer:
      'Short-form video is vertical video, usually under two minutes, made for mobile feeds like YouTube Shorts, TikTok, and Instagram Reels. It works best when it answers one question or makes one point quickly, with the hook in the first second and captions that make sense without sound.',
  },
  {
    question: 'Do I need paid software to edit short videos?',
    answer:
      'No. Free tools cover most needs, including the editors built into YouTube and Instagram and CapCut\'s $0 plan. Consider CapCut Pro at $19.99 a month only once you are publishing often enough that templates, extra caption styles, or export options save you real time each week.',
  },
  {
    question: 'Can AI edit my short videos for me?',
    answer:
      'AI can produce a usable first draft, but it cannot do your final review. Tools like YouTube\'s Edit with AI arrange your strongest moments automatically. You still need to check facts, caption accuracy, brand voice, music rights, and export format before posting, especially on a client\'s account.',
  },
  {
    question: 'How do I make more engaging short videos?',
    answer:
      'Put the problem or the payoff in the first second, cut every pause, and use large captions. Finish with one specific call to action. Track saves, shares, and completion rate instead of views, since those numbers show whether people found the video useful enough to keep or pass on.',
  },
];

const STEPS = [
  {
    number: 1,
    title: 'Pick one audience question and write its answer',
    minutes: 'Minutes 0 to 4',
    body: [
      'Write down a single question your audience asks, then answer it in one sentence. "How do I get clients to approve posts faster?" becomes "Send one link with every draft, a deadline, and a default-approve rule." I call this the one-question rule. If you cannot state the answer in one breath, the topic is two videos.',
      'Add two or three supporting points and stop there. Scope creep is the main reason a 30-minute video turns into a 90-minute one, because every extra point means another take, another cut, and another caption block.',
      <>
        There is a business reason to keep it this tight. Wyzowl's{' '}
        <a
          href="https://www.wyzowl.com/video-marketing-statistics/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-gray-900 underline hover:text-gray-700"
        >
          State of Video Marketing 2026
        </a>{' '}
        found that 63% of consumers would rather learn about a product or service from a short video
        than any other way. Viewers want the answer, and the backstory can wait.
      </>,
    ],
    pitfall:
      'Do not open the timer and then go looking for a topic. Keep a running list of real questions from client calls, DMs, and comments so this step starts with choosing an item.',
  },
  {
    number: 2,
    title: 'Write a script you can say in under 60 seconds',
    minutes: 'Minutes 4 to 8',
    body: [
      'Draft roughly 90 to 130 words, which most people say aloud in 40 to 60 seconds. Put the problem or the promised outcome in the first line. Leave background for later, or cut it.',
      'On length, half of video marketers (51%) told Wyzowl that 30 to 60 seconds works best, and 71% picked something between 30 seconds and two minutes. Start at 30 to 60 seconds, then let your own retention data move you.',
    ],
    structure: [
      'Hook (one line): the problem or result. "Your clients ghost approval requests. Here\'s the fix."',
      'Answer (one or two lines).',
      'Two or three supporting points, each a single sentence.',
      'One call to action.',
    ],
    pitfall:
      'Read the script aloud once with the timer running. If it goes past 60 seconds, delete a supporting point. Talking faster will not fix it.',
  },
  {
    number: 3,
    title: 'Record three vertical takes with pauses between points',
    minutes: 'Minutes 8 to 15',
    body: [
      'Hold the phone vertically at eye level and record the full script three times. Pause for a full second between points. Those pauses give you clean edit points, so you can join take one\'s hook to take three\'s ending without a jump mid-word.',
      'Frame yourself from mid-chest up and leave space above your head. YouTube Shorts, TikTok, and Instagram Reels all place interface buttons along the bottom and right edge, so keep your face and any on-screen text toward the center.',
      'Audio comes before lighting. Viewers forgive a slightly dim image, but they scroll past echo and hiss. If you have no mic, record in a small room with soft furnishings and hold the phone within an arm\'s length.',
    ],
    pitfall:
      'Stop after three takes. A fourth take rarely beats the best moments of the first three, and it uses time you need for editing.',
  },
];

export default function ShortFormVideoGuidePage() {
  const { isLoggedIn } = useLoaderData<typeof loader>();

  const breadcrumbJsonLd = generateBreadcrumbJsonLd([
    { name: 'Home', url: generateCanonicalUrl('/') },
    { name: 'Blog', url: generateCanonicalUrl('/blog') },
    {
      name: 'Short-Form Video in Under 30 Minutes',
      url: generateCanonicalUrl(`/blog/${SLUG}`),
    },
  ]);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: TITLE,
    description:
      'A fixed 30-minute workflow for making one short-form video, from picking a question to publishing.',
    image: generateCanonicalUrl('/blog/how-to-make-a-short-form-video-hero.png'),
    datePublished: '2026-11-04',
    author: { '@type': 'Organization', name: SITE_NAME },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: generateCanonicalUrl('/'),
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />

      <Header variant="static" isLoggedIn={isLoggedIn} />

      {/* Breadcrumbs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <nav className="flex items-center text-sm text-gray-500" aria-label="Breadcrumb">
          <Link to="/" className="flex items-center hover:text-gray-700 transition-colors">
            <HomeIcon className="w-4 h-4 mr-1" />
            Home
          </Link>
          <ChevronRightIcon className="w-4 h-4 mx-2" />
          <Link to="/blog" className="hover:text-gray-700 transition-colors">
            Blog
          </Link>
          <ChevronRightIcon className="w-4 h-4 mx-2" />
          <span className="text-gray-900 font-medium">Short-Form Video in Under 30 Minutes</span>
        </nav>
      </div>

      {/* Hero Section */}
      <section className="relative py-12 lg:py-20 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.4]"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgb(156 163 175) 1px, transparent 0)`,
            backgroundSize: '24px 24px',
            maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)',
            WebkitMaskImage:
              'radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)',
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 mb-6">
              <ClockIcon className="w-5 h-5 text-gray-700" />
              <span className="text-sm font-semibold text-gray-700">30-Minute Guide</span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-6 leading-tight">
              {TITLE}
            </h1>

            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              You have a client meeting at 3:00, a webinar recording nobody has touched, and a Slack
              message asking for "something quick for Reels." Thirty minutes is enough if you work to
              a fixed time budget and answer one audience question in a single vertical video.
            </p>

            <p className="mt-6 text-sm text-gray-500">Nov 4, 2026 · 12 min read</p>
          </div>
        </div>
      </section>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <figure className="mb-12 not-prose">
          <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-lg">
            <picture>
              <source srcSet="/blog/how-to-make-a-short-form-video-hero.avif" type="image/avif" />
              <source srcSet="/blog/how-to-make-a-short-form-video-hero.webp" type="image/webp" />
              <img
                src="/blog/how-to-make-a-short-form-video-hero.png"
                alt="How to make a short-form video in under 30 minutes in 2026"
                className="w-full"
              />
            </picture>
          </div>
        </figure>

        <article className="prose prose-lg max-w-none">
          <p className="text-lg text-gray-600 mb-10 leading-relaxed">
            Spend four minutes choosing the topic, four on the script, seven recording a few takes,
            nine editing and captioning, four checking quality, and the last two writing the caption
            and publishing. You need a phone and a free editor. The method is beginner-friendly, and
            most of the skill is in what you leave out.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">The short version</h2>
          <p className="text-gray-600 mb-6 leading-relaxed">
            To make a short-form video fast, limit it to one question and one clear answer. Write a
            script of about 100 words, record several vertical takes with pauses between points, and
            cut the best one together with readable captions. Check audio, framing, and the hook,
            then write a platform-specific caption last. The 30-minute figure is a schedule you set
            for yourself. No platform publishes it as a benchmark.
          </p>

          <div className="bg-white border border-gray-200 rounded-2xl p-6 my-8 not-prose">
            <h3 className="font-bold text-gray-900 mb-3">Before you start</h3>
            <ul className="space-y-2">
              {[
                'A phone with at least 5 GB free and the lens wiped clean',
                'A quiet room, or a wired or clip-on mic',
                'One editing app installed and logged in (options are in the table in Step 4)',
                'Your brand fonts, caption style, and end card saved as a template',
                'A timer you can see',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-600 text-sm leading-relaxed mt-4">
              The template matters more than it looks. Rebuilding caption styles and export settings
              for every video costs five to ten minutes each time, and that is a third of your
              budget gone before you have recorded anything.
            </p>
          </div>

          {STEPS.map((step) => (
            <section key={step.number} className="mt-12">
              <div className="flex items-center gap-3 mb-2">
                <span className="w-9 h-9 rounded-lg bg-gray-900 text-white flex items-center justify-center font-bold flex-shrink-0">
                  {step.number}
                </span>
                <span className="text-sm font-medium text-gray-400">{step.minutes}</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Step {step.number}: {step.title}
              </h2>
              {step.body.map((para, i) => (
                <p key={i} className="text-gray-600 mb-6 leading-relaxed">
                  {para}
                </p>
              ))}
              {step.structure && (
                <div className="bg-white border border-gray-200 rounded-2xl p-6 my-6 not-prose">
                  <p className="font-semibold text-gray-900 mb-3">A workable structure:</p>
                  <ol className="space-y-2 list-decimal list-inside">
                    {step.structure.map((line) => (
                      <li key={line} className="text-gray-700">
                        {line}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
              <div className="border-l-4 border-gray-900 bg-gray-50 rounded-r-xl px-5 py-3 my-6 not-prose">
                <p className="text-gray-700 text-sm leading-relaxed">
                  <span className="font-semibold text-gray-900">Pitfall: </span>
                  {step.pitfall}
                </p>
              </div>
            </section>
          ))}

          <section className="mt-12">
            <div className="flex items-center gap-3 mb-2">
              <span className="w-9 h-9 rounded-lg bg-gray-900 text-white flex items-center justify-center font-bold flex-shrink-0">
                4
              </span>
              <span className="text-sm font-medium text-gray-400">Minutes 15 to 24</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Step 4: Cut, caption, and add audio
            </h2>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Load all three takes into your editor and assemble the strongest lines in order. Cut
              every pause, breath, and "um." Then generate auto-captions and fix them by hand.
            </p>
            <p className="text-gray-600 mb-6 leading-relaxed">
              AI is good at first drafts. YouTube announced Edit with AI on November 6, 2025, a
              feature that builds a first draft of a Short by picking and arranging strong moments
              from footage in your camera roll.{' '}
              <a
                href="https://about.fb.com/news/2026/04/one-year-of-edits-built-for-and-with-creators/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-900 underline hover:text-gray-700"
              >
                Meta's April 2026 update
              </a>{' '}
              pushed Instagram's Edits app into planning as well, adding saved Reels and audio,
              notes, insights, and weekly idea suggestions. Adoption is moving quickly: 63% of video marketers now use AI
              to create or edit video, up from 51% a year earlier, though the share reporting good
              ROI fell from 93% to 82% over the same period, per Wyzowl. Faster production has not
              made the videos better.
            </p>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Creators on X say the same thing from experience. AI lowers costs, but polished
              animations and complete edits are often neither easy nor fast. Let AI produce the rough
              cut and the first pass of captions, and do the judgment work yourself.
            </p>

            <div className="overflow-x-auto my-8 not-prose">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-gray-500">
                    <th className="py-3 pr-4 font-semibold">Tool</th>
                    <th className="py-3 pr-4 font-semibold">Cost</th>
                    <th className="py-3 pr-4 font-semibold">Best for</th>
                    <th className="py-3 font-semibold">Watch out for</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  {[
                    [
                      'YouTube Shorts (Edit with AI)',
                      'Free in the YouTube app',
                      'Quick first drafts from camera-roll clips',
                      'Output is built around YouTube, so check framing before reposting elsewhere',
                    ],
                    [
                      'Instagram Edits',
                      'See site',
                      'Reels-first creators who also want idea tracking',
                      'Planning features are tied to Instagram',
                    ],
                    [
                      <a
                        href="https://www.capcut.com/pricing/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-900 underline hover:text-gray-700"
                      >
                        CapCut
                      </a>,
                      'Free $0, Pro $19.99/mo, Ultra $79/mo',
                      'Precise cuts, caption styling, templates',
                      'CapCut notes that final pricing varies by account, offers, and taxes',
                    ],
                    [
                      'Canva',
                      <a
                        href="https://www.canva.com/pricing/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-900 underline hover:text-gray-700"
                      >
                        See site
                      </a>,
                      'Branded text overlays and end cards for teams',
                      'Less precise for cutting dialogue',
                    ],
                    [
                      'Adobe Express',
                      <a
                        href="https://www.adobe.com/express/pricing"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-900 underline hover:text-gray-700"
                      >
                        See site
                      </a>,
                      'Teams already working in Adobe',
                      'Templates can look generic unless you customize them',
                    ],
                  ].map((row, i) => (
                    <tr key={i} className="border-b border-gray-100 align-top">
                      <td className="py-3 pr-4 font-medium text-gray-900">{row[0]}</td>
                      <td className="py-3 pr-4">{row[1]}</td>
                      <td className="py-3 pr-4">{row[2]}</td>
                      <td className="py-3">{row[3]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-gray-600 mb-6 leading-relaxed">
              My recommendation: if you make videos weekly, CapCut's free tier plus a saved
              caption template is enough. Pay for a plan once the template work, not the editing, is
              what slows you down.
            </p>

            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-5 my-6 not-prose">
              <LightBulbIcon className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-gray-700 text-sm leading-relaxed">
                Captions should be large, high-contrast, and limited to three to five words per line.
                Readable captions, clean audio, vertical framing, and a strong hook do more for a
                video than transitions or effects when you only have 30 minutes.
              </p>
            </div>
          </section>

          <section className="mt-12">
            <div className="flex items-center gap-3 mb-2">
              <span className="w-9 h-9 rounded-lg bg-gray-900 text-white flex items-center justify-center font-bold flex-shrink-0">
                5
              </span>
              <span className="text-sm font-medium text-gray-400">Minutes 24 to 28</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Step 5: Run a four-minute quality check
            </h2>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Watch the full video once with sound and once muted, and check it against this list:
            </p>
            <ul className="space-y-3 mb-6 not-prose">
              {[
                'Does the first second state the problem or the outcome?',
                'Are all facts, figures, and claims correct?',
                'Do the captions match the spoken words exactly, including names and numbers?',
                'Is the audio clear with no clipping?',
                'Do you have rights to the music and any third-party footage?',
                'Does it sound like your brand or your client\'s brand?',
                'Is the export 9:16 vertical, with nothing important hidden behind platform buttons?',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-600 mb-6 leading-relaxed">
              This step is where AI output needs the most attention. Auto-captions misspell product
              names, and auto-selected music may not be licensed for commercial use. For agency work,
              one wrong caption on a client's account leads to an awkward conversation that takes
              longer to resolve than the whole edit.
            </p>
            <div className="border-l-4 border-gray-900 bg-gray-50 rounded-r-xl px-5 py-3 my-6 not-prose">
              <p className="text-gray-700 text-sm leading-relaxed">
                <span className="font-semibold text-gray-900">Pitfall: </span>
                The muted pass is where most people find mistakes. A large share of short-form
                viewing happens with sound off, so the captions have to carry the video on their own.
              </p>
            </div>
          </section>

          <section className="mt-12">
            <div className="flex items-center gap-3 mb-2">
              <span className="w-9 h-9 rounded-lg bg-gray-900 text-white flex items-center justify-center font-bold flex-shrink-0">
                6
              </span>
              <span className="text-sm font-medium text-gray-400">Minutes 28 to 30</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Step 6: Write the caption and publish
            </h2>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Write the post caption and call to action after the video is finished, so the text
              matches what viewers saw. Tailor it to each platform. TikTok captions can be short and
              conversational, YouTube Shorts benefit from a searchable title, and Instagram Reels
              need the call to action near the start because the caption truncates.
            </p>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Pick one call to action and make it specific. Community threads on X point out that
              Shorts can bring views without converting viewers into long-form subscribers or
              customers. "Follow for more" seldom drives anything. "Grab the approval checklist in
              our bio" gives viewers a reason to act.
            </p>
            <p className="text-gray-600 mb-6 leading-relaxed">
              When the same video goes to several accounts, the manual work happens here: one upload
              per app, plus a separate login for each client profile.{' '}
              <Link to="/features" className="text-gray-900 font-semibold hover:underline">
                TheTabber
              </Link>{' '}
              posts one finished video to 9+ platforms from a single screen with captions adapted per
              network, which brings those two minutes back within budget.
            </p>
          </section>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-12">Troubleshooting</h2>

          <h3 className="text-lg font-bold text-gray-900 mb-2 mt-8">
            Why does my video keep running past 60 seconds?
          </h3>
          <p className="text-gray-600 mb-6 leading-relaxed">
            The topic is too broad. Go back to Step 1 and split it. A video that answers two
            questions should be two videos, and you will have tomorrow's topic already chosen.
          </p>

          <h3 className="text-lg font-bold text-gray-900 mb-2 mt-8">
            What if the AI first draft cuts in the wrong places?
          </h3>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Use it to sort clips, then rebuild the hook yourself. Editors on r/VideoEditors describe
            short-form work as unusually demanding because of fast pacing and frequent cuts.
            Automatic cut points often land mid-word, so move them to the pauses you recorded in
            Step 3.
          </p>

          <h3 className="text-lg font-bold text-gray-900 mb-2 mt-8">
            How do I turn a horizontal webinar into a short?
          </h3>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Find a 30 to 60 second passage that answers one question on its own, crop to 9:16
            centered on the speaker, and add captions plus a new opening line that states the
            problem. Creators on r/NewTubers ask about this often. The step people skip is the new
            hook: a clip that depends on earlier context loses viewers in the first second.
          </p>

          <h3 className="text-lg font-bold text-gray-900 mb-2 mt-8">
            Why do my shorts get views but no clicks?
          </h3>
          <p className="text-gray-600 mb-8 leading-relaxed">
            Your call to action is vague, or there is not one. Name a single next step and say it out
            loud as well as putting it in the caption.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-12">Next steps</h2>
          <p className="text-gray-600 mb-6 leading-relaxed">
            After seven days, judge each video on retention, completion rate, saves, shares,
            comments, profile visits, and conversions. Views alone do not tell you much. Then turn
            the parts that repeat into templates: hook formats, caption style, framing marks on the
            floor, export presets, and your call-to-action lines. Wyzowl puts video use at 91% of
            businesses, so speed alone will not set you apart. Teams that publish regularly and learn
            from their numbers will. If you want to try remixing finished clips, YouTube's{' '}
            <a
              href="https://blog.youtube/news-and-events/reimagine-new-ai-powered-remix-tool-youtube-shorts/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-900 underline hover:text-gray-700"
            >
              Shorts Reimagine
            </a>
            , launched March 18, 2026, turns a frame from an eligible Short into a new
            8-second clip from a prompt and up to two photo references.
          </p>

        </article>

        <FAQ items={FAQS} align="left" className="mt-12" innerClassName="" />

        <section className="bg-gray-900 text-white rounded-2xl p-8 lg:p-12 mt-16 text-center">
          <h2 className="text-3xl font-bold mb-4">Publish one video everywhere in minutes</h2>
          <p className="text-lg text-gray-300 mb-6">
            Once your short is done, TheTabber posts it to 9+ platforms from a single screen with
            captions adapted per network. Try it free for 3 days and get those last two minutes back.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {isLoggedIn ? (
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-2 bg-white text-gray-900 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-all"
              >
                Go to Dashboard
                <ArrowRightIcon className="w-5 h-5" />
              </Link>
            ) : (
              <>
                <Link
                  to="/auth/email/signup"
                  className="inline-flex items-center gap-2 bg-white text-gray-900 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-all"
                >
                  Start Free Trial
                  <ArrowRightIcon className="w-5 h-5" />
                </Link>
                <Link
                  to="/blog"
                  className="bg-gray-800 text-white px-8 py-4 rounded-xl font-semibold hover:bg-gray-700 transition-all"
                >
                  Read More Guides
                </Link>
              </>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
